import { useState, useEffect, useCallback } from 'react';

import { getGroupTransforms, getTranslateValues } from '../../utils/transformHelpers';
import { STATIC_TABLE_ELEMENT } from '../../utils/complexDocumentConstants';

const useContextMenu = (documentId, scaling, multiSelectedElementIds, textBlock, staticTable, disableVariables = false) => {
  const [xPos, setXPos] = useState('0px');
  const [yPos, setYPos] = useState('0px');
  const [transform, setTransform] = useState('translate(0, 0)');
  const [elementId, setElementId] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [element, setElement] = useState(null);
  const [showMultiElementsMenu, setShowMultiElementsMenu] = useState(false);
  const [isSingleElementOfGroupSelected, setIsSingleElementOfGroupSelected] = useState(false);

  const handleContextMenu = useCallback(
    e => {
      if (!e) return;
      e.preventDefault();
      const moveableAreaElement = e.target.closest('.moveable-area');
      const selectableElement = e.target.closest('.selectable');
      if (moveableAreaElement && multiSelectedElementIds && multiSelectedElementIds.length > 1) {
        const target = moveableAreaElement.parentElement;
        const { x, y } = getTranslateValues(target);
        const targetRect = target.getBoundingClientRect();
        const shiftX = (e.clientX > targetRect.left + targetRect.width / 2) ? '-100%' : '0%';
        const shiftY = (e.clientY + 320 > window.innerHeight) ? '-100%' : '0%';
        
        setElement(null);
        setXPos(`${e.clientX}px`);
        setYPos(`${e.clientY}px`);
        setTransform(`translate(${shiftX}, ${shiftY})`);
        setElementId('');
        setShowMenu(true);
        setShowMultiElementsMenu(true);
        setIsSingleElementOfGroupSelected(false);
      } else if (selectableElement) {
        let targetElement = selectableElement;
        let left = 0, top = 0;
        if (targetElement.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
          if (e.target.tagName !== 'TD') {
            //when clicking in between the static table element, table cell could be null.
            const tableCell = e.target.closest('TD') || {};
            left = tableCell.offsetLeft || 0;
            top = tableCell.offsetTop || 0;
          } else {
            left = e.target.offsetLeft;
            top = e.target.offsetTop;
          }
        }
        if (targetElement.classList.contains('block-selectable')) {
          targetElement = targetElement.parentElement.parentElement;
        }
        console.log("e.target :: ", e.target, e.offsetY);
        const targetElementId = targetElement.getAttribute('data-uuid');
        setElement(targetElement);
        const { x, y } = getTranslateValues(targetElement);
        if (x && y && targetElementId && multiSelectedElementIds.length && multiSelectedElementIds.includes(targetElementId)) {
          const targetRect = targetElement.getBoundingClientRect();
          const shiftX = (e.clientX > targetRect.left + targetRect.width / 2) ? '-100%' : '0%';
          const shiftY = (e.clientY + 320 > window.innerHeight) ? '-100%' : '0%';

          setXPos(`${e.clientX}px`);
          setYPos(`${e.clientY}px`);
          setTransform(`translate(${shiftX}, ${shiftY})`);
          setElementId(targetElementId);
          setShowMenu(true);
          setShowMultiElementsMenu(false);
          if (multiSelectedElementIds.length > 1) {
            setIsSingleElementOfGroupSelected(true);
          }
        } else if (staticTable) {
          targetElement = e.target.closest('TABLE');
          const tableBoundary = targetElement.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          const shiftX = (e.clientX > targetRect.left + targetRect.width / 2) ? '-100%' : '0%';
          const shiftY = (e.clientY + 320 > window.innerHeight) ? '-100%' : '0%';

          setXPos(`${e.clientX}px`);
          setYPos(`${e.clientY}px`);
          setTransform(`translate(${shiftX}, ${shiftY})`);
          setElementId(targetElementId);
          setShowMenu(true);
          setShowMultiElementsMenu(false);
          setIsSingleElementOfGroupSelected(false);
        }
      } else if (textBlock) {

        let editorRef = textBlock.editorRef;

        if (disableVariables) return;

        if (editorRef) {
          let editor = editorRef?.current?.getEditor();
          let range = editor?.getSelection();

          if (range) {
            // Position near the selected text
            let boundary = editor?.getBounds(editor?.getSelection());
            let rootRect = editor.root.getBoundingClientRect();

            let leftPos = rootRect.left + boundary.left + boundary.width;
            let topPos = rootRect.top + boundary.top;
            const menuHeight = 320;

            const shiftX = (leftPos + 230 > window.innerWidth) ? '-100%' : '0%';
            const shiftY = (topPos + menuHeight > window.innerHeight) ? '-100%' : '0%';

            setXPos(`${leftPos}px`);
            setYPos(`${topPos}px`);
            setTransform(`translate(${shiftX}, ${shiftY})`);
          } else {
            // No selection — open at cursor position
            const shiftX = (e.clientX + 230 > window.innerWidth) ? '-100%' : '0%';
            const shiftY = (e.clientY + 320 > window.innerHeight) ? '-100%' : '0%';
            setXPos(`${e.clientX}px`);
            setYPos(`${e.clientY}px`);
            setTransform(`translate(${shiftX}, ${shiftY})`);
          }
        } else {
          // No editor ref — open at cursor position
          const shiftX = (e.clientX + 230 > window.innerWidth) ? '-100%' : '0%';
          const shiftY = (e.clientY + 320 > window.innerHeight) ? '-100%' : '0%';
          setXPos(`${e.clientX}px`);
          setYPos(`${e.clientY}px`);
          setTransform(`translate(${shiftX}, ${shiftY})`);
        }
        setShowMenu(true);

      } else {
        setShowMenu(false);
        setShowMultiElementsMenu(false);
        setIsSingleElementOfGroupSelected(false);
      }
    },
    [scaling, multiSelectedElementIds, disableVariables, documentId],
  );

  const handleClick = useCallback(() => {
    setShowMenu(previousValue => {
      if (previousValue) return false;
      return previousValue;
    }) 
  }, []);

  const handleScroll = useCallback(() => {
    setShowMenu(previousValue => {
      if (previousValue) return false;
      return previousValue;
    });
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleClick, handleScroll]);

  useEffect(() => {
    if (document.getElementById(documentId)) {
      document
        .getElementById(documentId)
        .addEventListener('contextmenu', handleContextMenu);
    }
    if (document.getElementsByClassName('moveable-area')?.[0]) {
      document
        .getElementsByClassName('moveable-area')[0]
        .addEventListener('contextmenu', handleContextMenu);
    }

    return () => {
      if (document.getElementById(documentId)) {
        document
          .getElementById(documentId)
          .removeEventListener('contextmenu', handleContextMenu);
      }
      if (document.getElementsByClassName('moveable-area')?.[0]) {
        document
          .getElementsByClassName('moveable-area')[0]
          .removeEventListener('contextmenu', handleContextMenu);
      }
    };
  }, [showMenu, documentId, handleClick, handleContextMenu]);
  return { xPos, yPos, transform, elementId, showMenu, element, showMultiElementsMenu, setShowMenu, isSingleElementOfGroupSelected };
};

export default useContextMenu;
